import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ScrollView,
  Alert,
  Modal,
  Platform,
} from 'react-native';
import { createChildProfile, ChildProfile } from '../../types/profile';
import { useUserStore } from '../../contexts/StoreContext';
import { appConfig } from '../../config/configManager';

interface AddProfileViewProps {
  onProfileCreated: (profile: ChildProfile) => void;
  onCancel: () => void;
}

export const AddProfileView: React.FC<AddProfileViewProps> = ({
  onProfileCreated,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    dob: new Date(2024, 11, 1), // December 1, 2024 - about 1 year old
    gender: '' as 'male' | 'female' | '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDatePicker, setShowDatePicker] = useState(false);
  
  // Date picker state
  const [tempDate, setTempDate] = useState({
    day: 1, // January 1st
    month: 1, // January
    year: 2025, // Current year
  });
  
  // Default user email — from the authenticated account session
  const userStore = useUserStore();
  const defaultUserEmail = userStore?.()?.session?.user?.email ?? '';

  // Helper function to format date for display
  const formatDateForDisplay = (date: Date): string => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to handle date selection
  const handleDateChange = () => {
    const selectedDate = new Date(tempDate.year, tempDate.month - 1, tempDate.day);
    setFormData({ ...formData, dob: selectedDate });
    setShowDatePicker(false);
  };

  // Initialize temp date when picker opens
  const openDatePicker = () => {
    const currentDob = formData.dob;
    setTempDate({
      day: currentDob.getDate(),
      month: currentDob.getMonth() + 1,
      year: currentDob.getFullYear(),
    });
    setShowDatePicker(true);
  };

  // Helper to generate day/month/year options
  const getDayOptions = () => {
    const daysInMonth = new Date(tempDate.year, tempDate.month, 0).getDate();
    return Array.from({ length: daysInMonth }, (_, i) => i + 1);
  };

  // Helper to handle month change and adjust day if needed
  const handleMonthChange = (month: number) => {
    const daysInNewMonth = new Date(tempDate.year, month, 0).getDate();
    const newDay = tempDate.day > daysInNewMonth ? daysInNewMonth : tempDate.day;
    setTempDate({ ...tempDate, month, day: newDay });
  };

  // Helper to handle year change and adjust day if needed (for leap years)
  const handleYearChange = (year: number) => {
    const daysInMonth = new Date(year, tempDate.month, 0).getDate();
    const newDay = tempDate.day > daysInMonth ? daysInMonth : tempDate.day;
    setTempDate({ ...tempDate, year, day: newDay });
  };

  const getMonthOptions = () => [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = currentYear; year >= currentYear - 18; year--) {
      years.push(year);
    }
    return years;
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Profile name is required';
    }

    // Validate date of birth
    const now = new Date();
    if (formData.dob > now) {
      newErrors.dob = 'Date of birth cannot be in the future';
    }
    
    const maxAge = new Date(now.getFullYear() - 18, now.getMonth(), now.getDate());
    if (formData.dob < maxAge) {
      newErrors.dob = 'Child must be under 18 years old';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    try {
      // Generate a unique account number
      const accountNum = `CHILD${Date.now().toString().slice(-6)}`;

      console.log('📋 AddProfileView: Creating profile with DOB:', formData.dob);
      console.log('📋 AddProfileView: DOB ISO string:', formData.dob.toISOString());

      // Create the profile using the factory function
      const profileWithoutId = createChildProfile({
        name: formData.name.trim(),
        email: defaultUserEmail, // Use app user's email by default
        dob: formData.dob.toISOString(),
        accountnum: accountNum,
        gender: (formData.gender as 'male' | 'female') || 'other', // Default to 'other' if not selected
        isPremium: false, // App subscription controls this
        devfloorCeilingTimestamp: {
          communication: new Date().toISOString(),
          motor: new Date().toISOString(),
          finemotor: new Date().toISOString(),
          grossmotor: new Date().toISOString(),
          cognitive: new Date().toISOString(),
          socialemotional: new Date().toISOString(),
        },
      });

      // Add an ID to complete the profile
      const newProfile: ChildProfile = {
        ...profileWithoutId,
        id: `profile_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      };

      onProfileCreated(newProfile);
    } catch (error) {
      Alert.alert('Error', 'Failed to create profile. Please try again.');
      console.error('Profile creation error:', error);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.backgroundImageContainer}>
        <View style={styles.backgroundOverlay} />
      </View>
      <ScrollView style={styles.scrollContainer} contentContainerStyle={styles.contentContainer}>
      <View style={{ maxWidth: appConfig.getSingleCardMaxWidth(), width: '100%', alignSelf: 'center' }}>
      <View style={styles.header}>
        <Text style={styles.title}>Add New Child Profile</Text>
        <Text style={styles.subtitle}>
          Create a profile to track your child's development milestones
        </Text>
      </View>

      <View style={styles.form}>
        {/* Name Field */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Child's Profile Name *</Text>
          <TextInput
            style={[styles.input, errors.name ? styles.inputError : null]}
            value={formData.name}
            onChangeText={(text) => setFormData({ ...formData, name: text })}
            placeholder="Name for this profile"
            autoCapitalize="words"
          />
          {errors.name && <Text style={styles.errorText}>{errors.name}</Text>}
        </View>

        {/* Date of Birth */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Date of Birth *</Text>
          <TouchableOpacity
            style={[styles.dateInput, errors.dob ? styles.inputError : null]}
            onPress={openDatePicker}
          >
            <Text style={styles.dateText}>
              {formatDateForDisplay(formData.dob)}
            </Text>
          </TouchableOpacity>
          {errors.dob && <Text style={styles.errorText}>{errors.dob}</Text>}
          <Text style={styles.helpText}>
            Tap to select date of birth
          </Text>
        </View>

        {/* Gender Selection */}
        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Gender</Text>
          <View style={styles.genderContainer}>
            {(['male', 'female'] as const).map((gender) => (
              <TouchableOpacity
                key={gender}
                style={[
                  styles.genderButton,
                  formData.gender === gender ? styles.genderButtonSelected : null,
                ]}
                onPress={() => setFormData({ ...formData, gender })}
              >
                <Text
                  style={[
                    styles.genderButtonText,
                    formData.gender === gender ? styles.genderButtonTextSelected : null,
                  ]}
                >
                  {gender.charAt(0).toUpperCase() + gender.slice(1)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          {errors.gender && <Text style={styles.errorText}>{errors.gender}</Text>}
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity style={styles.cancelButton} onPress={onCancel}>
            <Text style={styles.cancelButtonText}>Cancel</Text>
          </TouchableOpacity>
          <TouchableOpacity style={styles.createButton} onPress={handleSubmit}>
            <Text style={styles.createButtonText}>Create Profile</Text>
          </TouchableOpacity>
        </View>
      </View>
      </View>

      {/* Enhanced Date Picker Modal */}
      <Modal
        visible={showDatePicker}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowDatePicker(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.datePickerContainer}>
            <Text style={styles.datePickerTitle}>Select Date of Birth</Text>
            
            {/* Date Selection Grid */}
            <View style={styles.dateSelectionContainer}>
              
              {/* Day Selection */}
              <View style={styles.dateComponentContainer}>
                <Text style={styles.dateComponentLabel}>Day</Text>
                <ScrollView style={styles.dateScrollView} showsVerticalScrollIndicator={false}>
                  {getDayOptions().map((day) => (
                    <TouchableOpacity
                      key={day}
                      style={[
                        styles.dateOptionButton,
                        tempDate.day === day ? styles.dateOptionButtonSelected : null
                      ]}
                      onPress={() => setTempDate({ ...tempDate, day })}
                    >
                      <Text style={[
                        styles.dateOptionText,
                        tempDate.day === day ? styles.dateOptionTextSelected : null
                      ]}>
                        {day}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Month Selection */}
              <View style={styles.dateComponentContainer}>
                <Text style={styles.dateComponentLabel}>Month</Text>
                <ScrollView style={styles.dateScrollView} showsVerticalScrollIndicator={false}>
                  {getMonthOptions().map((month) => (
                    <TouchableOpacity
                      key={month.value}
                      style={[
                        styles.dateOptionButton,
                        tempDate.month === month.value ? styles.dateOptionButtonSelected : null
                      ]}
                      onPress={() => handleMonthChange(month.value)}
                    >
                      <Text style={[
                        styles.dateOptionText,
                        tempDate.month === month.value ? styles.dateOptionTextSelected : null
                      ]}>
                        {month.label}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>

              {/* Year Selection */}
              <View style={styles.dateComponentContainer}>
                <Text style={styles.dateComponentLabel}>Year</Text>
                <ScrollView style={styles.dateScrollView} showsVerticalScrollIndicator={false}>
                  {getYearOptions().map((year) => (
                    <TouchableOpacity
                      key={year}
                      style={[
                        styles.dateOptionButton,
                        tempDate.year === year ? styles.dateOptionButtonSelected : null
                      ]}
                      onPress={() => handleYearChange(year)}
                    >
                      <Text style={[
                        styles.dateOptionText,
                        tempDate.year === year ? styles.dateOptionTextSelected : null
                      ]}>
                        {year}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </ScrollView>
              </View>
            </View>

            {/* Selected Date Preview */}
            <View style={styles.selectedDatePreview}>
              <Text style={styles.selectedDateText}>
                Selected: {new Date(tempDate.year, tempDate.month - 1, tempDate.day).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </Text>
            </View>

            <View style={styles.datePickerButtonContainer}>
              <TouchableOpacity
                style={styles.datePickerCancelButton}
                onPress={() => setShowDatePicker(false)}
              >
                <Text style={styles.datePickerCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.datePickerOkButton}
                onPress={handleDateChange}
              >
                <Text style={styles.datePickerOkText}>OK</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1e293b', // Same as welcome screen
  },
  scrollContainer: {
    flex: 1,
    zIndex: 1, // Ensure scroll content appears above background
  },
  contentContainer: {
    flexGrow: 1,
    minHeight: '100%', // Ensure content takes full height
  },
  backgroundImageContainer: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: '#3b82f6', // Rich blue background
  },
  backgroundOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(59, 130, 246, 0.15)', // Light blue overlay for depth
  },
  header: {
    padding: 24,
    backgroundColor: 'transparent',
    borderBottomWidth: 0,
    zIndex: 1, // Ensure content appears above background
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff', // White text
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 2 },
    textShadowRadius: 4,
  },
  subtitle: {
    fontSize: 16,
    color: '#e2e8f0', // Light gray for subtitle
    lineHeight: 24,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  form: {
    padding: 24,
    zIndex: 1, // Ensure content appears above background
  },
  fieldContainer: {
    marginBottom: 24,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff', // White labels
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  input: {
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent white border
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white background
    color: '#1f2937', // Dark text for readability
  },
  inputError: {
    borderColor: '#fca5a5', // Lighter red for better contrast
    backgroundColor: 'rgba(254, 226, 226, 0.9)', // Light red tint
  },
  errorText: {
    color: '#fca5a5', // Lighter red for better contrast
    fontSize: 14,
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.5)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  helpText: {
    color: '#cbd5e1', // Lighter gray for help text
    fontSize: 14,
    marginTop: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 1,
  },
  genderContainer: {
    flexDirection: 'row',
    gap: 16,
  },
  genderButton: {
    flex: 1,
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: 8,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    alignItems: 'center',
  },
  genderButtonSelected: {
    borderColor: '#10b981', // Green for selected state
    backgroundColor: '#10b981',
  },
  genderButtonText: {
    fontSize: 16,
    color: '#1f2937', // Dark text for readability
    fontWeight: '500',
  },
  genderButtonTextSelected: {
    color: '#ffffff',
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 16,
    marginTop: 32,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.5)',
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: 'transparent',
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff', // White text
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 0, height: 1 },
    textShadowRadius: 2,
  },
  createButton: {
    flex: 1,
    paddingVertical: 16,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#10b981', // Green for create action
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Date picker styles
  dateInput: {
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.3)', // Semi-transparent white border
    borderRadius: 8,
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.9)', // Semi-transparent white background
    justifyContent: 'center',
  },
  dateText: {
    fontSize: 16,
    color: '#1f2937', // Dark text for readability
  },
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  datePickerContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    padding: 24,
    margin: 20,
    minWidth: 300,
  },
  datePickerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 16,
    textAlign: 'center',
  },
  dateSelectionContainer: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 20,
  },
  dateComponentContainer: {
    flex: 1,
    alignItems: 'center',
  },
  dateComponentLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 8,
  },
  dateScrollView: {
    maxHeight: 150,
    width: '100%',
  },
  dateOptionButton: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginVertical: 2,
    borderRadius: 6,
    backgroundColor: '#f9fafb',
    alignItems: 'center',
    minWidth: 60,
  },
  dateOptionButtonSelected: {
    backgroundColor: '#3b82f6',
  },
  dateOptionText: {
    fontSize: 14,
    color: '#374151',
  },
  dateOptionTextSelected: {
    color: '#ffffff',
    fontWeight: '600',
  },
  selectedDatePreview: {
    padding: 12,
    backgroundColor: '#f0f9ff',
    borderRadius: 8,
    marginBottom: 16,
  },
  selectedDateText: {
    fontSize: 16,
    color: '#1e40af',
    textAlign: 'center',
    fontWeight: '500',
  },
  datePickerButtonContainer: {
    flexDirection: 'row',
    gap: 12,
  },
  datePickerCancelButton: {
    flex: 1,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#ffffff',
  },
  datePickerCancelText: {
    fontSize: 16,
    color: '#374151',
  },
  datePickerOkButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    backgroundColor: '#3b82f6',
  },
  datePickerOkText: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
  },
});